import { TAlternativeImage } from 'widgets/product/AlternativeImage';

type TAlternativeImageSource = {
    media: string;
    srcset: string;
};

/**
 * @description Base CoreAlternativeImage implementation
 * @param AlternativeImage Base widget for extending
 * @returns Alternative image view widget
 */
function CoreAlternativeImageClassCreator(AlternativeImage: TAlternativeImage) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreAlternativeImage
     * @augments AlternativeImage
     * @classdesc Alternative image component that mixed to ProductTile widget.
     * It implement possibility to show alternative product image on tile in form of hover slider (on desktop)
     * and carousel with swapping (on mobile/tablet). It designed to be as lightweight as possible to not blow up very
     * heavy PLP page. For product carousels (e.g. "Recently Viewed" or "Recommendations" carousel), we have disabled
     * the feature with pictures swapping(on mobile/tablet) since we have double scrolling.
     * For this reasons in mind alternative image do not stored into markup it added dynamically on user
     * actions (hover, scroll). As approach for proper image size fetch (viewport, pixels density etc) we
     * use front-end url generation. As base for it we use basic image size - the most proper size that browser
     * choose from proposed sources. To get this image we use `currentSrc` HTMLImageElement property.
     * @property {string} data-widget - Widget name `productTile`
     * @property {string} data-alt-image-url - alternative image src
     * @property {string} data-alt-image-title - alternative image title
     */
    class CoreAlternativeImage extends AlternativeImage {
        /**
         * @description Prepare alternative image viewport dependent src.
         * The trick is get already defined by browser image from set and prepare same image for alt.
         * If project has completely different images on breakpoints this should be extended with
         * update image src on viewport change.
         * @returns alternative image url
         */
        getAlternativeImageSrc(): string {
            if (!this.has('alternativeViewContainer')) {
                return '';
            }

            const altImage = this.ref('alternativeViewContainer').attr('data-alt-image-url');

            if (!altImage) {
                return '';
            }

            const altImageUrl = new URL(altImage, window.location.origin);

            const baseImage = this.ref('tileImage').get();

            if (!(baseImage instanceof HTMLImageElement)) {
                return '';
            }

            const baseImageUrl = baseImage.currentSrc || baseImage.src;
            const baseImageUrlParsed = new URL(baseImageUrl, window.location.origin);
            const disQueryString = baseImageUrlParsed.search;

            return altImageUrl.origin + altImageUrl.pathname + disQueryString;
        }

        /**
         * @description Returns responsive source data for the alternative image.
         * @returns alternative image sources
         */
        getAlternativeImageSources(): Array<TAlternativeImageSource> {
            if (!this.has('alternativeViewContainer')) {
                return [];
            }

            return ['lg', 'md', 'sm'].reduce((sources: Array<TAlternativeImageSource>, viewType) => {
                const srcset = this.ref('alternativeViewContainer').attr(`data-alt-image-srcset-${viewType}`);
                const media = this.ref('alternativeViewContainer').attr(`data-alt-image-media-${viewType}`);

                if (typeof srcset === 'string' && srcset) {
                    sources.push({
                        media: typeof media === 'string' ? media : '',
                        srcset
                    });
                }

                return sources;
            }, []);
        }

        /**
         * @description Insert img tag to alternative image picture container
         */
        insertAlternativeImage() {
            if (this.imageInserted) {
                return;
            }

            const alternativeImageSrc = this.getAlternativeImageSrc();

            if (!alternativeImageSrc) {
                return;
            }

            const altImageElement = document.createElement('img');

            altImageElement.src = alternativeImageSrc;
            altImageElement.alt = this.getAlternativeImageText();
            altImageElement.title = this.getAlternativeImageText();
            const alternativeImagePicture = this.ref('alternativeViewPicture').get(0);

            if (!alternativeImagePicture) {
                return;
            }

            this.getAlternativeImageSources().forEach(source => {
                const sourceElement = document.createElement('source');

                sourceElement.srcset = source.srcset;

                if (source.media) {
                    sourceElement.media = source.media;
                }

                alternativeImagePicture.append(sourceElement);
            });
            alternativeImagePicture.append(altImageElement);

            this.imageInserted = true;
        }
    }

    return CoreAlternativeImage;
}

export type TCoreAlternativeImage = ReturnType<typeof CoreAlternativeImageClassCreator>;

export type TCoreAlternativeImageInstance = InstanceType<TAlternativeImage>;

export default CoreAlternativeImageClassCreator;
